/**
 * @fileoverview Hook for managing Plan Centro de Costo business logic
 * Provides CRUD operations and state management for production task planning
 */

import { useState, useCallback } from 'react';
import { pb } from '../helpers/pocketbase_helper';
import { 
  PlanCentroCosto, 
  PlanCentroCostoExpanded,
  CreatePlanCentroCostoData, 
  UpdatePlanCentroCostoData,
  PLAN_CENTRO_COSTO_COLLECTION 
} from '../types/collections/PlanCentroCosto';
import { CentroCosto, CentroCostoExpanded } from '../types/collections/CentroCosto';

/**
 * Hook for managing Plan Centro de Costo operations
 * @returns Object with state and functions for CRUD operations
 */
export const usePlanificador = () => {
  // State management
  const [planes, setPlanes] = useState<PlanCentroCostoExpanded[]>([]);
  const [centrosCosto, setCentrosCosto] = useState<CentroCostoExpanded[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  /**
   * Fetch all plans with expanded relationships
   */
  const fetchPlanes = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      

      const result = await pb.collection(PLAN_CENTRO_COSTO_COLLECTION.name).getList(1, 1000, {
        sort: "-created",
        expand: "centro_costo_peso.centro_costo.maquina,centro_costo_peso.centro_costo.encargado,centro_costo_peso.centro_costo.proceso,centro_costo_peso.centro_costo.operario"
      });
      
      const planesWithData = result.items;

      setPlanes(planesWithData as unknown as PlanCentroCostoExpanded[]);
    } catch (err: any) {
      console.error("Error fetching planes:", err);
      setError("Error al cargar planes");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch available cost centers for plan creation
   */
  const fetchCentrosCosto = useCallback(async () => {
    try {
      const result = await pb.collection("centro_costo").getList(1, 1000, {
        sort: "nombre",
        expand: "maquina,encargado,proceso"
      });
      

      setCentrosCosto(result.items as unknown as CentroCostoExpanded[]);
    } catch (err: any) {
      console.error("Error fetching centros de costo:", err);
      setError("Error al cargar centros de costo");
    }
  }, []);

  /**
   * Create a new plan
   * @param data - Plan data for creation
   * @returns Object with success status and optional error
   */
  const createPlan = useCallback(async (data: CreatePlanCentroCostoData) => {
    try {
      setLoading(true);
      setError("");
      
      // Validate data before sending
      if (!data.nombre?.trim()) {
        return { success: false, error: "El nombre del plan es requerido" };
      }
      
      if (!data.centro_costo_peso || data.centro_costo_peso.length === 0) {
        return { success: false, error: "Debe seleccionar al menos un centro de costo" };
      }

      // Handle CentroCostoPeso data structure
      let centroCostoPesoIds: string[] = [];
      
      if (Array.isArray(data.centro_costo_peso) && data.centro_costo_peso.length > 0) {
        // Check if we have CentroCostoPeso objects or just strings
        if (typeof data.centro_costo_peso[0] === 'object' && 'centro_costo' in data.centro_costo_peso[0]) {
          // We have CentroCostoPeso objects - extract IDs and weights
          const centroCostoIds = data.centro_costo_peso.map((item: any) => item.centro_costo);
          const weights = data.centro_costo_peso.map((item: any) => item.peso || 0);
          
          // Create/update centro_costo_peso records
          centroCostoPesoIds = await createOrUpdateCentroCostoPeso(centroCostoIds, weights);
        } else {
          // We have string IDs - create centro_costo_peso records with weight 0
          const centroCostoIds = data.centro_costo_peso as string[];
          const weights = new Array(centroCostoIds.length).fill(0);
          centroCostoPesoIds = await createOrUpdateCentroCostoPeso(centroCostoIds, weights);
        }
      }

      // Create plan with centro_costo_peso IDs
      const planData = {
        ...data,
        centro_costo_peso: centroCostoPesoIds
      };

      const newPlan = await pb.collection(PLAN_CENTRO_COSTO_COLLECTION.name).create(planData);
      
      // Refresh planes list
      await fetchPlanes();
      
      return { success: true, data: newPlan };
    } catch (err: any) {
      console.error("Error creating plan:", err);
      
      // Handle specific backend errors
      if (err?.data?.data?.nombre?.code === "validation_not_unique") {
        return { success: false, error: "Ya existe un plan con ese nombre" };
      }
      
      if (err?.data?.data?.centro_costo_peso?.code === "validation_required") {
        return { success: false, error: "Debe seleccionar al menos un centro de costo" };
      }
      
      return { success: false, error: "Error al crear el plan" };
    } finally {
      setLoading(false);
    }
  }, [fetchPlanes]);

  /**
   * Update an existing plan
   * @param id - Plan ID to update
   * @param data - Updated plan data
   * @returns Object with success status and optional error
   */
  const updatePlan = useCallback(async (id: string, data: UpdatePlanCentroCostoData) => {
    try {
      setLoading(true);
      setError("");
      
      // Validate data before sending
      if (data.nombre !== undefined && !data.nombre.trim()) {
        return { success: false, error: "El nombre del plan es requerido" };
      }
      
      if (data.centro_costo_peso !== undefined && data.centro_costo_peso.length === 0) {
        return { success: false, error: "Debe seleccionar al menos un centro de costo" };
      }

      // Obtener el plan actual para comparar los centro_costo_peso
      let currentPlan;
      try {
        currentPlan = await pb.collection(PLAN_CENTRO_COSTO_COLLECTION.name).getOne(id);

      } catch (error) {

        // Continuamos sin el plan actual
      }

      // Handle CentroCostoPeso data structure
      let planData = { ...data };
      let oldCentroCostoPesoIds: string[] = [];
      let newCentroCostoPesoIds: string[] = [];
      
      // Guardar los IDs actuales para compararlos después
      if (currentPlan && currentPlan.centro_costo_peso && Array.isArray(currentPlan.centro_costo_peso)) {
        oldCentroCostoPesoIds = [...currentPlan.centro_costo_peso];
      }
      
      if (data.centro_costo_peso && Array.isArray(data.centro_costo_peso) && data.centro_costo_peso.length > 0) {
        // Check if we have CentroCostoPeso objects or just strings
        if (typeof data.centro_costo_peso[0] === 'object' && 'centro_costo' in data.centro_costo_peso[0]) {
          // We have CentroCostoPeso objects - extract IDs and weights
          const centroCostoIds = data.centro_costo_peso.map((item: any) => item.centro_costo);
          const weights = data.centro_costo_peso.map((item: any) => item.peso || 0);
          
          // Create/update centro_costo_peso records
          newCentroCostoPesoIds = await createOrUpdateCentroCostoPeso(centroCostoIds, weights);
          
          // Update plan data with centro_costo_peso IDs
          planData = {
            ...data,
            centro_costo_peso: newCentroCostoPesoIds
          };
        } else {
          // We have string IDs - create centro_costo_peso records with weight 0
          const centroCostoIds = data.centro_costo_peso as string[];
          const weights = new Array(centroCostoIds.length).fill(0);
          newCentroCostoPesoIds = await createOrUpdateCentroCostoPeso(centroCostoIds, weights);
          
          // Update plan data with centro_costo_peso IDs
          planData = {
            ...data,
            centro_costo_peso: newCentroCostoPesoIds
          };
        }
      }

      // Actualizar el plan con los nuevos IDs
      const updatedPlan = await pb.collection(PLAN_CENTRO_COSTO_COLLECTION.name).update(id, planData);
      
      // Identificar y eliminar registros huérfanos
      // (registros que estaban en el plan pero ya no están)
      const orphanedIds = oldCentroCostoPesoIds.filter(id => !newCentroCostoPesoIds.includes(id));
      if (orphanedIds.length > 0) {

        
        // Eliminar cada registro huérfano
        for (const orphanId of orphanedIds) {
          try {
            // Verificar si el registro está siendo usado en otro plan antes de eliminarlo
            const usageResult = await pb.collection(PLAN_CENTRO_COSTO_COLLECTION.name).getList(1, 10, {
              filter: `centro_costo_peso ~ "${orphanId}" && id != "${id}"`
            });
            
            if (usageResult.totalItems === 0) {
              // No se usa en ningún otro plan, es seguro eliminarlo

              await pb.collection("centro_costo_peso").delete(orphanId);
            } else {

            }
          } catch (error) {

            // Continuamos con los siguientes registros
          }
        }
      }
      
      // Refresh planes list
      await fetchPlanes();
      
      return { success: true, data: updatedPlan };
    } catch (err: any) {
      console.error("Error updating plan:", err);
      
      // Handle specific backend errors
      if (err?.data?.data?.nombre?.code === "validation_not_unique") {
        return { success: false, error: "Ya existe un plan con ese nombre" };
      }
      
      return { success: false, error: "Error al actualizar el plan" };
    } finally {
      setLoading(false);
    }
  }, [fetchPlanes]);

  /**
   * Soft delete a plan (mark as inactive)
   * @param id - Plan ID to delete
   * @returns Object with success status and optional error
   */
  const deletePlan = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError("");
      
      await pb.collection(PLAN_CENTRO_COSTO_COLLECTION.name).update(id, { activo: false });
      
      // Refresh planes list
      await fetchPlanes();
      
      return { success: true };
    } catch (err: any) {
      console.error("Error deleting plan:", err);
      return { success: false, error: "Error al eliminar el plan" };
    } finally {
      setLoading(false);
    }
  }, [fetchPlanes]);

  /**
   * Restore a deleted plan (mark as active)
   * @param id - Plan ID to restore
   * @returns Object with success status and optional error
   */
  const restorePlan = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError("");
      
      await pb.collection(PLAN_CENTRO_COSTO_COLLECTION.name).update(id, { activo: true });
      
      // Refresh planes list
      await fetchPlanes();
      
      return { success: true };
    } catch (err: any) {
      console.error("Error restoring plan:", err);
      return { success: false, error: "Error al restaurar el plan" };
    } finally {
      setLoading(false);
    }
  }, [fetchPlanes]);

  /**
   * Get plan by ID with expanded relationships
   * @param id - Plan ID to retrieve
   * @returns Plan object or null if not found
   */
  const getPlanById = useCallback(async (id: string) => {
    try {
      const plan = await pb.collection(PLAN_CENTRO_COSTO_COLLECTION.name).getOne(id, {
        expand: "centro_costo_peso.centro_costo.maquina,centro_costo_peso.centro_costo.encargado,centro_costo_peso.centro_costo.proceso"
      });
      return plan;
    } catch (err: any) {
      console.error("Error getting plan by ID:", err);
      return null;
    }
  }, []);

  /**
   * Search plans by name
   * @param searchTerm - Term to search for in plan names
   * @returns Filtered plans array
   */
  const searchPlanes = useCallback(async (searchTerm: string) => {
    try {
      setLoading(true);
      setError("");
      
      const result = await pb.collection(PLAN_CENTRO_COSTO_COLLECTION.name).getList(1, 1000, {
        filter: `nombre ~ "${searchTerm}" && activo = true`,
        sort: "-created",
        expand: "centro_costo_peso.centro_costo.maquina,centro_costo_peso.centro_costo.encargado,centro_costo_peso.centro_costo.proceso"
      });
      
      setPlanes(result.items as unknown as PlanCentroCostoExpanded[]);
    } catch (err: any) {
      console.error("Error searching plans:", err);
      setError("Error al buscar planes");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError("");
  }, []);

  /**
   * Fetch centro_costo_peso data by ID
   * @param id - ID of centro_costo_peso to fetch
   * @returns CentroCostoPeso object with expanded centro_costo
   */
  const fetchCentroCostoPesoData = useCallback(async (id: string) => {
    try {
      const result = await pb.collection("centro_costo_peso").getOne(id, {
        expand: "centro_costo.maquina,centro_costo.encargado,centro_costo.proceso"
      });

      return result;
    } catch (err: any) {

      return null;
    }
  }, []);

  /**
   * Reload all data
   */
  const reloadData = useCallback(async () => {
    await Promise.all([fetchPlanes(), fetchCentrosCosto()]);
  }, [fetchPlanes, fetchCentrosCosto]);

  /**
   * Validate weight percentages
   * @param weights - Array of weight percentages
   * @returns Object with validation result and error message
   */
  const validateWeights = useCallback((weights: number[]) => {
    // Check individual weights (0-100)
    for (const weight of weights) {
      if (weight < 0 || weight > 100) {
        return { valid: false, error: "Los porcentajes deben estar entre 0 y 100" };
      }
    }
    
    // Check total sum (≤ 100)
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (total > 100) {
      return { valid: false, error: `La suma total (${total}%) no puede exceder 100%` };
    }
    
    return { valid: true, total };
  }, []);

  /**
   * Create new centro_costo_peso records for each plan
   * @param centroCostoIds - Array of centro_costo IDs
   * @param weights - Array of corresponding weights
   * @returns Array of centro_costo_peso IDs
   */
  const createOrUpdateCentroCostoPeso = useCallback(async (centroCostoIds: string[], weights: number[]) => {
    try {
      const centroCostoPesoIds: string[] = [];
      
      // Primero verificamos que todos los IDs de centro_costo sean válidos
      for (const centroCostoId of centroCostoIds) {
        try {
          // Verificar que el centro_costo exista
          await pb.collection("centro_costo").getOne(centroCostoId);
        } catch (error) {
          throw new Error(`Centro de costo con ID ${centroCostoId} no existe`);
        }
      }
      
      // Procesar cada centro_costo - siempre crear nuevos registros
      for (let i = 0; i < centroCostoIds.length; i++) {
        const centroCostoId = centroCostoIds[i];
        const peso = weights[i] || 0;
        
        // Crear un nuevo registro para cada centro_costo
        try {
          const created = await pb.collection("centro_costo_peso").create({
            centro_costo: centroCostoId,
            peso: peso
          });
          centroCostoPesoIds.push(created.id);
        } catch (error) {
          throw new Error(`Error al crear centro_costo_peso para ${centroCostoId}`);
        }
      }
      
      return centroCostoPesoIds;
    } catch (err: any) {

      throw new Error("Error al guardar los porcentajes de peso");
    }
  }, []);

  return {
    // State
    planes,
    centrosCosto,
    loading,
    error,
    
    // CRUD operations
    fetchPlanes,
    fetchCentrosCosto,
    createPlan,
    updatePlan,
    deletePlan,
    restorePlan,
    getPlanById,
    
    // Weight management
    validateWeights,
    createOrUpdateCentroCostoPeso,
    
    // Centro costo peso operations
    fetchCentroCostoPesoData,
    
    // Utility functions
    searchPlanes,
    clearError,
    reloadData
  };
};
